import type { Bridge } from '@casa/bridge';

// `device.keepAwake` for a browser (decision §11: the Screen Wake Lock where the browser offers it).
// The Tilez Hub that carries a browser's bridge has no screen, so the page keeps itself awake. A
// browser releases the lock whenever the page is hidden; it is taken again when the page comes back.

interface WakeLockSentinel {
    released: boolean;
    release() : Promise<void>;
}

interface WakeLockPage {
    navigator: { wakeLock?: { request(type: 'screen') : Promise<WakeLockSentinel> } };
    document: { visibilityState: string; addEventListener(type: 'visibilitychange', listener: () => void) : void };
}

/** The bridge, with `device.keepAwake` answered in the page. */
export function withPageWakeLock(bridge: Bridge, page: WakeLockPage) : Bridge {
    let wanted = false;
    let lock: WakeLockSentinel | undefined;

    const take = async () : Promise<void> => {
        if (!wanted || page.document.visibilityState !== 'visible' || (lock && !lock.released)) {
            return;
        }

        const wakeLock = page.navigator.wakeLock;
        if (!wakeLock) {
            throw new Error('this browser cannot keep the screen on');
        }

        lock = await wakeLock.request('screen');
    };

    page.document.addEventListener('visibilitychange', () => {
        // Taken again when the page is shown; a refusal then (low battery, say) is not an error to report.
        take().catch(() => {});
    });

    return {
        ...bridge,
        device: {
            ...bridge.device,
            async keepAwake(enabled) {
                wanted = enabled;
                if (enabled) {
                    await take();

                    return;
                }

                const held = lock;
                lock = undefined;
                await held?.release();
            },
        },
    };
}
